/**
 * Island key for the Builder page body.
 *
 * Lives in its own `.ts` module because `crash-safe-ssr-island.tsx` may only
 * export components (`react-refresh/only-export-components`).
 *
 * `CrashSafeSsrIsland` snapshots SSR markup by DOM id ACROSS the hydration
 * boundary, so its key has to satisfy two requirements that pull against each
 * other. Each was violated once, by a separate shipped bug:
 *
 *  1. IDENTICAL on server and client for one page. The key was first derived
 *     from `useId()`, which does not agree across hydration here:
 *
 *       server  id="ssr-island-_R_agv3ln5_"
 *       client  id="ssr-island-_r_m_"
 *
 *     `getElementById` missed, the snapshot came back empty, and the homepage
 *     body went BLANK for ~966ms after it had already painted (COM-192).
 *
 *  2. DIFFERENT per page. The fix for (1) was a constant, which introduced the
 *     opposite bug: on a client-side navigation the OUTGOING page's island is
 *     still mounted when the incoming page first renders, so `getElementById`
 *     found it and the new page snapshotted the PREVIOUS page's markup.
 *     Reproduced on develop — clicking a homepage category link:
 *
 *       url    /  ->  /patiently/fsa-weight-loss.html      (changed)
 *       title  "Shop Over 2500..." -> "GLP-1 Weight Loss"  (changed)
 *       island 869,320 chars -> 869,320 chars              (IDENTICAL)
 *       h1     "Shop FSA Store®..." -> unchanged
 *
 *     The route loaded and the metadata updated while the body kept rendering
 *     the homepage until a hard refresh.
 *
 * Keying on the path satisfies both: the loader hands the same `path` to server
 * and client, and distinct pages get distinct ids.
 */

/**
 * Build the island key for a Builder page body.
 *
 * Non-alphanumerics collapse to `-` so the id stays a simple token. Distinct
 * paths colliding is harmless in practice: a collision only matters while two
 * islands are mounted at once, which is just the outgoing/incoming pair during
 * a single navigation.
 */
export function builderPageIslandKey(path: string): string {
  const slug = path.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `builder-page-body-${slug || "root"}`;
}
